// src/hooks/useAddCandidate.js
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ADD_CANDIDATE,
  SHEDULE_CANDIDATE,
  GET_EMPLOYEE_DETAILS,
  UPDATE_PROGRESS_STATUS,
  GET_LITE_EMPLOYEE_DETAILS,
  deleteEmployee,
  resignEmployee,
  GetCandidateScheduledList,
  SendEmail,
  UPDATE_FINAL_STATUS,
  DELETE_INTERVIEW_APPLICATION
} from '../api/index';

export const useAddCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ADD_CANDIDATE,
    onSuccess: () => {
      queryClient.invalidateQueries(['candidates']);
    },
    onError: (error) => {
      console.error('Error adding candidate:', error);
    },
  });
};

export const useDeleteScheduledCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: DELETE_INTERVIEW_APPLICATION,
    onSuccess: () => {
      queryClient.invalidateQueries(['candidateScheduledList']);
      queryClient.invalidateQueries(['appliedCandidates']);
    },
    onError: (error) => {
      console.error('Error deleting scheduled candidate:', error);
      const code = error?.response?.status;
      alert(
        code === 403
          ? 'You do not have permission to delete candidates (HR/admin only).'
          : 'Could not delete the candidate. Please try again.'
      );
    },
  });
};

export const useGetCandidateScheduledList = () => {
  return useQuery({
    queryKey: ['candidateScheduledList'],
    queryFn: GetCandidateScheduledList,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    onError: (error) => {
      console.error('Error fetching scheduled list:', error);
    },
  });
};

export const useScheduleCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: SHEDULE_CANDIDATE,
    onSuccess: () => {
      queryClient.invalidateQueries(['schedule']);
      queryClient.invalidateQueries(['candidateScheduledList']);
    },
    onError: (error) => {
      console.error('Error scheduling candidate:', error);
    },
  });
};

export const useEmployeeDetails = () => {
  return useQuery({
    queryKey: ['employeeDetails'],
    queryFn: GET_EMPLOYEE_DETAILS,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    onError: (error) => {
      console.error('Error fetching employee details:', error);
    },
  });
};

export const useAllEmployeeDetails = () => {
  return useQuery({
    queryKey: ['employeeList'],
    queryFn: GET_LITE_EMPLOYEE_DETAILS,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    placeholderData: (prev) => prev,
    refetchOnMount: 'always',
  });
};

export const useAppliedCandidates = () => {
  return useQuery({
    queryKey: ['appliedCandidates'],
    queryFn: async () => {
      const response = await fetch('https://www1.adityatrading.in/careers/api/applied-candidates/');
      if (!response.ok) throw new Error('Failed to fetch applied candidates');
      const data = await response.json();
      return data.data || [];
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
  });
};

export const useLiteAllEmployeeDetails = () => {
  return useQuery({
    queryKey: ['employeeListLite'],
    queryFn: GET_LITE_EMPLOYEE_DETAILS,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    cacheTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
  });
};

export const useUpdateProgressStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UPDATE_PROGRESS_STATUS,
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeList']);
      queryClient.invalidateQueries(['candidateScheduledList']);
      queryClient.invalidateQueries(['appliedCandidates']);
      // User feedback (success alert / round-email preview) is handled at the
      // call site in ProgressModel so it can branch on the response.
    },
    onError: (error) => {
      console.error('Error updating progress status:', error);
    },
  });
};

export const useUpdateFinalStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UPDATE_FINAL_STATUS,
    onSuccess: () => {
      queryClient.invalidateQueries(['candidateScheduledList']);
    },
    onError: (error) => {
      console.error('Error updating final status:', error);
      alert('Could not update the candidate outcome.');
    },
  });
};

export const useSendInvitationEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: SendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeList']);
      queryClient.invalidateQueries(['candidateScheduledList']);
      alert("Sent Succesfully");
    },
    onError: (error) => {
      console.error('Error updating progress status:', error);
      alert("Error While Sending");
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeList']);
      queryClient.invalidateQueries(['candidateScheduledList']);
    },
    onError: (error) => {
      console.error('Error deleting employee:', error);
    },
  });
};

export const useResignEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resignEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries(['employeeList']);
      queryClient.invalidateQueries(['resignedEmp']);
    },
    onError: (error) => {
      console.error('Error resigning employee:', error);
    },
  });
};
